import React, { useState, useEffect } from 'react';
import { Bell, MessageSquare, CheckSquare, Calendar, Plane, Clock, X, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationPanel = ({ assistantId, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, [assistantId]);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/notifications`, {
        headers: { 'x-assistant-session': assistantId },
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/assistant/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'x-assistant-session': assistantId },
        
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/assistant/notifications/mark-all-read`, {
        method: 'POST',
        headers: { 'x-assistant-session': assistantId },
        
      });
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Route based on type and link
    if (notification.link_url) {
      onClose();
      navigate(notification.link_url);
    } else {
      // Default routing based on type
      switch (notification.type) {
        case 'message':
          navigate('/messages');
          break;
        case 'task':
          navigate('/to-do-center');
          break;
        case 'calendar':
          navigate('/calendar');
          break;
        case 'travel':
          navigate('/travel-binder');
          break;
        case 'time':
          navigate('/messages'); // HR/Payroll thread
          break;
        default:
          break;
      }
      onClose();
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageSquare size={18} color="#3B82F6" />;
      case 'task':
        return <CheckSquare size={18} color="#10B981" />;
      case 'calendar':
        return <Calendar size={18} color="#8B5CF6" />;
      case 'travel':
        return <Plane size={18} color="#F59E0B" />;
      case 'time':
        return <Clock size={18} color="#EC4899" />;
      default:
        return <Bell size={18} color="#6B7280" />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          zIndex: 9998,
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: '60px',
          right: '20px',
          background: '#FFFFFF',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          width: '400px',
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 100px)',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Bell size={20} color="#3B82F6" />
            <h3 style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: 0 }}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <span style={{
                padding: '2px 8px',
                background: '#3B82F6',
                color: '#FFFFFF',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {unreadCount}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  color: '#6B7280',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Check size={14} />
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#6B7280',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          minHeight: '200px'
        }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              Loading notifications...
            </div>
          )}

          {!loading && notifications.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <Bell size={40} color="#E5E7EB" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>
                No notifications yet
              </p>
              <p style={{ color: '#9CA3AF', fontSize: '13px', marginTop: '8px' }}>
                You'll see updates about messages, tasks, calendar events, and more here
              </p>
            </div>
          )}

          {!loading && notifications.length > 0 && (
            <div>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    padding: '14px 16px',
                    borderBottom: '1px solid #F3F4F6',
                    cursor: 'pointer',
                    background: notification.is_read ? '#FFFFFF' : '#F0F9FF',
                    transition: 'background 0.15s',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = notification.is_read ? '#F9FAFB' : '#E0F2FE';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notification.is_read ? '#FFFFFF' : '#F0F9FF';
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: '#F9FAFB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {getIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                      <h4 style={{
                        color: '#0A0A0A',
                        fontSize: '14px',
                        fontWeight: notification.is_read ? '500' : '600',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {notification.title}
                      </h4>
                      {!notification.is_read && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#3B82F6',
                          flexShrink: 0,
                          marginTop: '4px'
                        }} />
                      )}
                    </div>
                    <p style={{
                      color: '#6B7280',
                      fontSize: '13px',
                      margin: '0 0 6px 0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {notification.message}
                    </p>
                    <span style={{
                      color: '#9CA3AF',
                      fontSize: '12px'
                    }}>
                      {formatTime(notification.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationPanel;
